import { ChatCircleDotsIcon } from "@phosphor-icons/react/dist/csr/ChatCircleDots";
import type { ColumnDef } from "@tanstack/react-table";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import type { PayrollStatementNotificationRecipient } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import { getNotificationReasonLabel } from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";

export const TechnicianCol = (
  /** Technicians with an open PackChat thread, marked so the row reads at a glance. */
  awaitingReplyTechnicianIds?: Set<string>,
): ColumnDef<PayrollStatementNotificationRecipient, unknown> => ({
  id: "technician",
  enableSorting: false,
  header: () => <span>Technician</span>,
  cell: ({ row }) => {
    const recipient = row.original;
    const ineligibleReason = getNotificationReasonLabel(
      recipient.ineligible_reason,
    );
    const awaitingFeedback = Boolean(
      awaitingReplyTechnicianIds?.has(recipient.technician_id),
    );

    return (
      <Stack align="start" gap={1} className="min-w-0">
        <Stack direction="row" align="center" gap={1} className="min-w-0">
          {awaitingFeedback && (
            <Tooltip>
              <TooltipTrigger>
                <ChatCircleDotsIcon
                  className="size-4 shrink-0 text-warning"
                  aria-label="Has an open PackChat thread"
                  role="img"
                />
              </TooltipTrigger>
              <TooltipContent>Open thread. See PackChat.</TooltipContent>
            </Tooltip>
          )}
          <Text variant="label" className="truncate" title={recipient.name}>
            {recipient.name}
          </Text>
        </Stack>
        {!recipient.eligible && ineligibleReason ? (
          <Text variant="caption" tone="destructive">
            {ineligibleReason}
          </Text>
        ) : (
          <Text
            variant="caption"
            tone="muted"
            className={cn({
              "font-mono": Boolean(recipient.technician_external_id),
            })}
          >
            {recipient.technician_external_id ?? "Unlinked technician"}
          </Text>
        )}
      </Stack>
    );
  },
});
